/**
 * Motor de reconhecimento facial (roda 100% no navegador).
 *
 * Usa @vladmandic/human com os modelos servidos localmente de
 * public/models (funciona na intranet, sem CDN):
 *  - blazeface  → detecção do rosto
 *  - facemesh/iris → malha e olhos (alinhamento e direção do olhar)
 *  - faceres    → descritor de 1024 floats (é o que o backend compara)
 *  - antispoof  → detecta foto-de-foto / tela
 *  - liveness   → detecta rosto "vivo"
 *
 * A lib é pesada (~2MB de JS + ~13MB de modelos), então o import é dinâmico:
 * nada disso entra no bundle inicial nem é baixado antes do primeiro uso da
 * câmera. A instância é um singleton — carregar os modelos uma vez só.
 */

// Mesmos limiares do demo FaceID oficial do human.
export const FACE_MIN_CONFIDENCE = 0.6;

let humanPromise = null;

const humanConfig = {
  modelBasePath: `${process.env.PUBLIC_URL || ''}/models`,
  cacheSensitivity: 0.01,
  filter: { enabled: true, equalization: true },
  debug: false,
  face: {
    enabled: true,
    detector: { rotation: true, return: false, mask: false, maxDetected: 2 },
    description: { enabled: true }, // faceres → embedding de 1024 floats
    mesh: { enabled: true },
    iris: { enabled: true },
    emotion: { enabled: false },
    antispoof: { enabled: true },
    liveness: { enabled: true },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: false },
  segmentation: { enabled: false },
};

/** Carrega (uma vez) e devolve a instância do Human pronta para usar. */
export async function getHuman() {
  if (!humanPromise) {
    humanPromise = (async () => {
      const mod = await import('@vladmandic/human');
      const HumanCtor = mod.Human || mod.default;
      const human = new HumanCtor(humanConfig);
      await human.load();
      await human.warmup();
      return human;
    })();
    // Se falhar (ex.: WebGL indisponível), permite tentar de novo depois.
    humanPromise.catch(() => {
      humanPromise = null;
    });
  }
  return humanPromise;
}

/**
 * Limiares de enquadramento. Rosto MAIOR e bem posicionado = descritor melhor
 * = menos recusa legítima. Calibrado para orientar o usuário a se aproximar,
 * centralizar, olhar de frente e ter boa luz.
 */
export const FRAMING = {
  minWidth: 0.22, // rosto deve ocupar >=22% da largura (senão: aproxime-se).
  // Sem limite superior de "muito perto": rosto grande = descritor melhor, e
  // cortar levemente não atrapalha. O foco da orientação é APROXIMAR.
  maxOffsetX: 0.24, // desvio horizontal do centro (generoso p/ não travar)
  maxOffsetY: 0.26, // desvio vertical do centro
  maxYaw: 0.52, // ~30° (rad) — permite a leve virada do cadastro, barra perfil
  maxPitch: 0.42, // ~24° (olhando muito p/ cima/baixo)
  darkBelow: 48, // luminância média do rosto muito baixa (ambiente escuro)
  backlitDiff: 85, // fundo muito mais claro que o rosto (contraluz)
  backlitFaceMax: 105,
};

/**
 * Mede a iluminação a partir do vídeo (amostra 96px). Devolve a luminância
 * média do quadro e da região do rosto — usado para avisar sobre ambiente
 * escuro ou contraluz (o vilão das capturas ao ar livre).
 */
export function estimateLighting(video, box) {
  try {
    if (!video || !video.videoWidth) return null;
    const w = 96;
    const h = Math.max(1, Math.round((w * video.videoHeight) / video.videoWidth));
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h).data;
    const lum = (i) => 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    let frameSum = 0;
    for (let i = 0; i < d.length; i += 4) frameSum += lum(i);
    const frameMean = frameSum / (w * h);
    let faceMean = frameMean;
    if (Array.isArray(box) && box.length >= 4) {
      const sx = w / video.videoWidth;
      const sy = h / video.videoHeight;
      const x0 = Math.max(0, Math.floor(box[0] * sx));
      const y0 = Math.max(0, Math.floor(box[1] * sy));
      const x1 = Math.min(w, Math.ceil((box[0] + box[2]) * sx));
      const y1 = Math.min(h, Math.ceil((box[1] + box[3]) * sy));
      let s = 0;
      let cnt = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          s += lum((y * w + x) * 4);
          cnt++;
        }
      }
      if (cnt > 0) faceMean = s / cnt;
    }
    return { frameMean, faceMean };
  } catch {
    return null;
  }
}

/**
 * Avalia o resultado de uma detecção (enquadramento + iluminação).
 *
 * A captura automática depende do ENQUADRAMENTO (rosto único, perto o
 * suficiente, centralizado, de frente) e da LUZ (nem escuro, nem contraluz) —
 * isso garante um bom descritor e reduz recusas legítimas. A vivacidade/
 * antispoof (real/live) continua ADVISORY (só aviso), para o sistema nunca
 * travar e sempre dar veredito.
 */
export function evaluateFace(result, videoWidth, videoHeight = 0, lighting = null) {
  const faces = result?.face || [];
  const face = faces[0];
  const box = Array.isArray(face?.box) ? face.box : [0, 0, 0, 0];
  const [bx, by, bw, bh] = box;
  const wr = videoWidth > 0 ? bw / videoWidth : 0;
  const cx = videoWidth > 0 ? (bx + bw / 2) / videoWidth : 0.5;
  const cy = videoHeight > 0 ? (by + bh / 2) / videoHeight : 0.5;
  const angle = face?.rotation?.angle || {};
  const yaw = typeof angle.yaw === 'number' ? Math.abs(angle.yaw) : 0;
  const pitch = typeof angle.pitch === 'number' ? Math.abs(angle.pitch) : 0;

  const checks = {
    oneFace: faces.length === 1,
    notTooFar: wr >= FRAMING.minWidth,
    centered:
      Math.abs(cx - 0.5) <= FRAMING.maxOffsetX &&
      Math.abs(cy - 0.5) <= FRAMING.maxOffsetY,
    frontal: yaw <= FRAMING.maxYaw && pitch <= FRAMING.maxPitch,
    faceScore: (face?.faceScore || face?.boxScore || 0) >= FACE_MIN_CONFIDENCE,
    hasDescriptor: Array.isArray(face?.embedding) && face.embedding.length > 0,
  };

  // Iluminação entra no gate quando o modal fornece a medição.
  let lightHint = null;
  if (lighting && faces.length === 1) {
    if (lighting.faceMean < FRAMING.darkBelow) {
      checks.wellLit = false;
      lightHint = 'dark';
    } else if (
      lighting.frameMean - lighting.faceMean > FRAMING.backlitDiff &&
      lighting.faceMean < FRAMING.backlitFaceMax
    ) {
      checks.wellLit = false;
      lightHint = 'backlit';
    } else {
      checks.wellLit = true;
    }
  }

  // Sinal informativo de vivacidade (não entra no gate).
  const real = typeof face?.real === 'number' ? face.real : null;
  const live = typeof face?.live === 'number' ? face.live : null;
  const liveness = {
    real,
    live,
    suspicious:
      (real !== null && real < FACE_MIN_CONFIDENCE) ||
      (live !== null && live < FACE_MIN_CONFIDENCE),
  };

  return {
    face,
    checks,
    liveness,
    lightHint,
    allOk: Object.values(checks).every(Boolean),
  };
}

/** Mensagem de orientação para o primeiro check de enquadramento/luz que falha. */
export function guidanceMessage(checks, lightHint = null) {
  if (!checks.oneFace) {
    return 'Deixe apenas UM rosto na câmera';
  }
  if (!checks.notTooFar) return 'Aproxime-se um pouco 👉';
  if (!checks.centered) return 'Centralize o rosto no círculo';
  if (!checks.frontal) return 'Olhe de frente para a câmera';
  if (checks.wellLit === false) {
    return lightHint === 'backlit'
      ? 'Evite luz forte atrás de você (contraluz)'
      : 'Ambiente escuro — procure mais luz';
  }
  if (!checks.faceScore) return 'Aproxime e olhe direto para a câmera';
  if (!checks.hasDescriptor) return 'Processando o rosto…';
  return 'Perfeito! Segure assim…';
}

/**
 * Congela o frame atual do vídeo num JPEG (máx. 640px de largura).
 * Devolve { blob, dataUrl } — o blob vai para o backend como evidência,
 * o dataUrl vira thumbnail na tela.
 */
export async function captureFrame(videoEl) {
  const maxWidth = 640;
  const scale = Math.min(1, maxWidth / (videoEl.videoWidth || maxWidth));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round((videoEl.videoWidth || maxWidth) * scale);
  canvas.height = Math.round((videoEl.videoHeight || 480) * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  return { blob, dataUrl };
}

/** Recomprime uma <img> já carregada num JPEG ≤maxWidth de largura. */
function recompress(img, maxWidth = 640, quality = 0.85) {
  const scale = Math.min(1, maxWidth / (img.naturalWidth || maxWidth));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round((img.naturalWidth || maxWidth) * scale);
  canvas.height = Math.round((img.naturalHeight || maxWidth) * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
}

/**
 * Extrai o descritor de uma IMAGEM estática (upload de arquivo no admin) e
 * devolve também o JPEG RECOMPRIMIDO (≤640px) — o descritor sai da imagem
 * original (qualidade máxima), mas só o blob leve vai para o backend, evitando
 * 413 no gateway e inchaço do PVC com fotos de celular de vários MB.
 * Lança erro se não achar exatamente um rosto.
 */
export async function describeImageFile(file) {
  const human = await getHuman();
  const imageUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error('Não foi possível ler a imagem'));
      img.src = imageUrl;
    });
    const result = await human.detect(img);
    const faces = result?.face || [];
    if (faces.length !== 1) {
      throw new Error(
        faces.length === 0
          ? 'Nenhum rosto encontrado na imagem'
          : 'A imagem tem mais de um rosto',
      );
    }
    const face = faces[0];
    if (!Array.isArray(face.embedding) || face.embedding.length === 0) {
      throw new Error('Não foi possível extrair o descritor facial da imagem');
    }
    const blob = (await recompress(img)) || file;
    return { descriptor: Array.from(face.embedding), face, blob };
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}
